import React, { useState } from "react";
import { Clock, MapPin, User, CalendarClock, CheckCircle2, XCircle, LogIn } from "lucide-react";

import { Button } from "src/components/ui/button";
import { useAuth } from "src/contexts/AuthContext";
import { useToast } from "src/components/ui/toast";
import { StatusBadge } from "src/features/site_visits/components/StatusBadge";
import { CheckInDialog } from "src/features/site_visits/components/CheckInDialog";
import { FeedbackDialog } from "src/features/site_visits/components/FeedbackDialog";
import { formatTime } from "src/features/site_visits/constants";
import { confirmSiteVisit, cancelSiteVisit } from "src/features/site_visits/api/siteVisitsApi";

export function VisitCard({ visit, onChanged }) {
  const { can } = useAuth();
  const { toast } = useToast();
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const canCheckIn = can("site_visits", "check_in");
  const canComplete = can("site_visits", "complete");
  const canUpdate = can("site_visits", "update");

  async function doConfirm() {
    setBusy(true);
    try {
      const updated = await confirmSiteVisit(visit.id);
      onChanged?.(updated);
    } catch {
      toast({ variant: "destructive", title: "Couldn't confirm" });
    } finally {
      setBusy(false);
    }
  }

  async function doCancel() {
    if (!window.confirm("Cancel this site visit?")) return;
    setBusy(true);
    try {
      const updated = await cancelSiteVisit(visit.id, "Cancelled by team");
      onChanged?.(updated);
    } catch {
      toast({ variant: "destructive", title: "Couldn't cancel" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-slate-900">
              {visit.lead?.name || "Lead"}
            </span>
            <StatusBadge value={visit.status} kind="status" />
            {visit.checkin_status && <StatusBadge value={visit.checkin_status} kind="checkin" />}
            {visit.outcome && <StatusBadge value={visit.outcome} kind="outcome" />}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> {formatTime(visit.scheduled_at)}
              {visit.duration_minutes ? ` · ${visit.duration_minutes} min` : ""}
            </span>
            {visit.project?.name && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" /> {visit.project.name}
              </span>
            )}
            {visit.host?.name && (
              <span className="flex items-center gap-1">
                <User className="h-3.5 w-3.5" /> {visit.host.name}
              </span>
            )}
          </div>

          {visit.feedback_notes && (
            <p className="mt-2 line-clamp-2 text-[12px] text-slate-600">“{visit.feedback_notes}”</p>
          )}
        </div>

        {/* Lifecycle actions */}
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {visit.status === "scheduled" && canUpdate && (
            <Button size="sm" variant="outline" onClick={doConfirm} disabled={busy}>
              <CalendarClock className="mr-1.5 h-3.5 w-3.5" /> Confirm
            </Button>
          )}
          {(visit.status === "scheduled" || visit.status === "confirmed") && canCheckIn && (
            <Button size="sm" onClick={() => setCheckInOpen(true)} disabled={busy}>
              <LogIn className="mr-1.5 h-3.5 w-3.5" /> Check in
            </Button>
          )}
          {visit.status === "checked_in" && canComplete && (
            <Button size="sm" onClick={() => setFeedbackOpen(true)} disabled={busy}>
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Complete
            </Button>
          )}
          {(visit.status === "scheduled" || visit.status === "confirmed") && canUpdate && (
            <Button
              size="sm"
              variant="ghost"
              className="text-rose-500 hover:text-rose-600"
              onClick={doCancel}
              disabled={busy}
            >
              <XCircle className="mr-1.5 h-3.5 w-3.5" /> Cancel
            </Button>
          )}
        </div>
      </div>

      <CheckInDialog
        open={checkInOpen}
        onOpenChange={setCheckInOpen}
        visit={visit}
        onCheckedIn={onChanged}
      />
      <FeedbackDialog
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        visit={visit}
        onCompleted={(res) => onChanged?.(res.site_visit)}
      />
    </div>
  );
}
